import type { Timeframe } from '#core/types/date-filter'
import { getDateRange } from '#core/utils/dates'
import { formatValue } from '@forgd/formatter'
import { defineStore } from 'pinia'

export type ViewModeOptionType = 'value' | 'percentile' | 'rank'

export const viewModeOptions: { label: string, shortLabel: string, value: ViewModeOptionType }[] = [
  { label: 'Show as Value', shortLabel: 'Value', value: 'value' },
  { label: 'Show as Percentile', shortLabel: 'Percentile', value: 'percentile' },
  { label: 'Show as Rank', shortLabel: 'Rank', value: 'rank' },
]

const TIMEFRAME_LABELS: Record<Timeframe | 'all', string> = {
  '24h': 'Last 24 hours',
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  '60d': 'Last 60 days',
  'all': 'All time',
}

interface MarketMakerBasicInfo {
  marketMakerId: string
  marketMakerName: string
  marketMakerImageUrl: string | null
}

interface FilterState {
  timeframe: Exclude<Timeframe, '24h'> | 'all'
}

const TIMEFRAME_SHORT_LABELS: Record<FilterState['timeframe'], string | null> = {
  '7d': '7D',
  '30d': '30D',
  '60d': '60D',
  'all': null,
}

const TIMEFRAME_BADGE_LABELS: Record<FilterState['timeframe'], string> = {
  '7d': 'Last 7D',
  '30d': 'Last 30D',
  '60d': 'Last 60D',
  'all': 'All time',
}

const defaultFilterState: FilterState = {
  timeframe: '30d',
}

export const useLeaderboardStore = defineStore('leaderboard', () => {
  const applyFiltersGlobally = ref(true)

  const viewMode = ref<ViewModeOptionType>('value')

  const isFilterSlideoverOpen = ref(false)
  const isMarketMakerDetailsSlideoverOpen = ref(false)
  const selectedMarketMaker = ref<MarketMakerBasicInfo | null>(null)

  const filterState = reactive<FilterState>({
    ...defaultFilterState,
  })

  const dateRange = computed(() => {
    if (filterState.timeframe === 'all') {
      return {
        startDate: undefined,
        endDate: undefined,
      }
    }

    const { start, end } = getDateRange(filterState.timeframe)

    return {
      startDate: formatValue(start, 'date:iso'),
      endDate: formatValue(end, 'date:iso'),
    }
  })

  const timeframeLabel = computed(() => {
    return TIMEFRAME_LABELS[filterState.timeframe]
  })

  const timeframeShortLabel = computed(() => {
    return TIMEFRAME_SHORT_LABELS[filterState.timeframe]
  })

  const timeframeBadgeLabel = computed(() => {
    return TIMEFRAME_BADGE_LABELS[filterState.timeframe]
  })

  function resetFilters() {
    Object.assign(filterState, defaultFilterState)
  }

  if (import.meta.client) {
    const route = useRoute()

    watch(
      () => route.path,
      (newPath, oldPath) => {
        if (!oldPath || oldPath === newPath) {
          return
        }

        const isLeaderboardPath = (path: string) => path.includes('/market-maker-leaderboard')
        if (!isLeaderboardPath(newPath) || !isLeaderboardPath(oldPath)) {
          return
        }

        viewMode.value = 'value'
        if (!applyFiltersGlobally.value) {
          resetFilters()
        }
      },
    )
  }

  function toggleFiltersSlideover() {
    isFilterSlideoverOpen.value = !isFilterSlideoverOpen.value
  }

  function openMarketMakerDetailsSlideover(marketMaker: MarketMakerBasicInfo) {
    selectedMarketMaker.value = marketMaker
    isMarketMakerDetailsSlideoverOpen.value = true
  }

  function closeMarketMakerDetailsSlideover() {
    selectedMarketMaker.value = null
    isMarketMakerDetailsSlideoverOpen.value = false
  }

  return {
    // State
    applyFiltersGlobally,
    dateRange,
    filterState,
    isFilterSlideoverOpen,
    isMarketMakerDetailsSlideoverOpen,
    selectedMarketMaker,
    timeframeLabel,
    timeframeBadgeLabel,
    timeframeShortLabel,
    viewMode,

    // Actions
    resetFilters,
    toggleFiltersSlideover,
    openMarketMakerDetailsSlideover,
    closeMarketMakerDetailsSlideover,
  }
})
